import { useState } from 'react';
import { Banknote, CreditCard, Loader2 } from 'lucide-react';
import ModalShell from '../../components/ui/ModalShell';
import { AKCENT, OK, CHYBA, kc, tint, Tlacitko, Nadpis, ocekavanaPlatba } from './ui';

/**
 * Dobírka u dveří (návrh obrazovek, 6). Velká částka a dvě dlaždice
 * způsobu platby — nic dalšího (pomocník „zákazník dal / vrátit" zrušen
 * 2026-10-12, zdržoval). Hotově → backend zaúčtuje do
 * pokladny; kartou → backend volá terminál a vrátí výsledek, zamítnutí nechá
 * okno otevřené s chybou.
 *
 * `onPay(zpusob)` vrací true/false.
 */
export default function PlatbaModal({ zastavka, onPay, onClose, isDark }) {
    // Předvybrané podle objednávky — zákazník si způsob zvolil už při objednání.
    const [zpusob, setZpusob] = useState(() => ocekavanaPlatba(zastavka));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    if (!zastavka) return null;
    const castka = Math.round(zastavka.castka_kc ?? 0);

    const zaplatit = async () => {
        setBusy(true);
        setErr(null);
        try {
            const ok = await onPay(zpusob);
            if (!ok) throw new Error(zpusob === 'kartou' ? 'Terminál platbu nepřijal. Zkus to znovu, nebo hotově.' : 'Platba se nezdařila.');
        } catch (e) {
            setErr(e?.message || String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalShell open onClose={onClose} zavritLze={!busy} isDark={isDark} nadpis="Zaplatit" ikona={Banknote} barva={AKCENT}
            paticka={
                <Tlacitko plne barva={OK} onClick={zaplatit} disabled={busy} vyska={60} style={{ width: '100%' }}>
                    {busy && <Loader2 size={18} className="animate-spin" />}
                    {busy && zpusob === 'kartou' ? 'Čekám na terminál…' : `Zaplaceno ${zpusob === 'hotove' ? 'hotově' : 'kartou'} · doručeno`}
                </Tlacitko>
            }>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ textAlign: 'center' }}>
                    <Nadpis barva={AKCENT}>K vybrání · #{zastavka.cislo_denni ?? zastavka.cislo_objednavky}</Nadpis>
                    <div style={{ fontSize: 40, fontWeight: 900, marginTop: 2 }}>{kc(castka)}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                    <Dlazdice Icon={Banknote} on={zpusob === 'hotove'} objednano={ocekavanaPlatba(zastavka) === 'hotove'} onClick={() => setZpusob('hotove')}>Hotově</Dlazdice>
                    <Dlazdice Icon={CreditCard} on={zpusob === 'kartou'} objednano={ocekavanaPlatba(zastavka) === 'kartou'} onClick={() => setZpusob('kartou')}>Kartou</Dlazdice>
                </div>

                {err && (
                    <div style={{ padding: '10px 12px', borderRadius: 12, fontSize: 13, fontWeight: 700, background: tint(CHYBA, 0.12), border: `1px solid ${tint(CHYBA, 0.45)}` }}>{err}</div>
                )}
            </div>
        </ModalShell>
    );
}

function Dlazdice({ Icon, on, objednano, onClick, children }) {
    return (
        <button type="button" onClick={onClick} aria-pressed={on} style={{
            minHeight: 76, borderRadius: 16, cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontSize: 16, fontWeight: 900,
            background: on ? tint(AKCENT, 0.14) : 'var(--surface-elevated)',
            color: on ? AKCENT : 'var(--text-primary)',
            border: `2px solid ${on ? AKCENT : 'var(--border-default)'}`,
            WebkitTapHighlightColor: 'transparent',
        }}>
            <Icon size={24} /> {children}
            {objednano && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>zvolil v objednávce</span>}
        </button>
    );
}
